import React from 'react';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import { Section, Heading, Badge, Button } from '../components/UI.jsx';
import { metadata, images } from '../data.js';
import { Activity, Heart, ShieldCheck, Zap } from 'lucide-react';
import { getOptimizedImageUrl } from '../lib/utils.js';

const Fysioterapi = () => {
  return (
    <div className="bg-[#F1E5DC] selection:bg-[#A8814E] selection:text-white">
      <Helmet>
        <title>Fysioterapi &amp; Behandling Frederiksberg | LK STUDIO</title>
        <meta name="description" content="Professionel fysioterapeutisk behandling, sportsmassage og genoptræning på Frederiksberg. Vi skræddersyr dit forløb efter dine behov. Book tid her." />
        <link rel="canonical" href="https://lkstudioems.com/fysioterapi" />

        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://lkstudioems.com/fysioterapi" />
        <meta property="og:title" content="Fysioterapi &amp; Behandling Frederiksberg | LK STUDIO" />
        <meta property="og:description" content="Professionel fysioterapeutisk behandling, sportsmassage og genoptræning på Frederiksberg. Vi skræddersyr dit forløb efter dine behov. Book tid her." />
        <meta property="og:image" content="https://assets.auxodev.dk/assets/766f1303-2002-4537-8cbb-d8bd7f1c5c0f/0c96b9e0-ed0f-4945-9b9c-cb0f556e3144.jpg" />
        <meta property="og:site_name" content="LK STUDIO" />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:url" content="https://lkstudioems.com/fysioterapi" />
        <meta name="twitter:title" content="Fysioterapi &amp; Behandling Frederiksberg | LK STUDIO" />
        <meta name="twitter:description" content="Professionel fysioterapeutisk behandling, sportsmassage og genoptræning på Frederiksberg. Vi skræddersyr dit forløb efter dine behov. Book tid her." />
        <meta name="twitter:image" content="https://assets.auxodev.dk/assets/766f1303-2002-4537-8cbb-d8bd7f1c5c0f/0c96b9e0-ed0f-4945-9b9c-cb0f556e3144.jpg" />

        {/* Structured Data */}
        <script type="application/ld+json">
          {`
            {
              "@context": "https://schema.org",
              "@type": "Service",
              "name": "Fysioterapi & Behandling",
              "provider": {
                "@type": "HealthAndBeautyBusiness",
                "name": "LK STUDIO",
                "url": "https://lkstudioems.com/",
                "telephone": "+4528852282",
                "address": {
                  "@type": "PostalAddress",
                  "streetAddress": "Engtoftevej 8",
                  "addressLocality": "Frederiksberg C",
                  "postalCode": "1816",
                  "addressCountry": "DK"
                }
              },
              "areaServed": "Frederiksberg",
              "description": "Professionel fysioterapeutisk behandling, sportsmassage og genoptræning på Frederiksberg. Vi skræddersyr dit forløb efter dine behov."
            }
          `}
        </script>
      </Helmet>
      {/* Hero Section */}
      <section className="relative min-h-[85vh] overflow-hidden bg-[#F1E5DC] pt-32 lg:pt-44 pb-16 md:pb-24">
        <div className="container relative z-10 mx-auto px-4 sm:px-6 lg:px-10">
          <div className="max-w-5xl">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
            >
              <Badge className="mb-8">
                BEHANDLING & GENOPTRÆNING
              </Badge>
              <h1 className="font-serif text-5xl font-normal leading-[0.9] tracking-[-0.05em] text-[#1C1B1A] sm:text-7xl lg:text-[7.5rem]">
                Fysioterapi hos <br />
                <span className="text-[#9A8E81] italic">LK STUDIO</span>
              </h1>
              
              <div className="mt-12 grid gap-10 border-t border-[#CEBDAA] pt-12 lg:grid-cols-[1.2fr_0.8fr]">
                <div className="max-w-2xl">
                  <p className="text-xl leading-relaxed text-[#494540] sm:text-2xl">
                    Som fysioterapeut ser jeg dine unikke behov og udfordringer. Vi skræddersyr et forløb, der er tilpasset dine mål, så du når dem mest effektivt.
                  </p>
                  <div className="mt-10 flex flex-wrap gap-4">
                    <Button 
                      variant="primary" 
                      onClick={() => window.open('https://booking.lkstudioems.com/book-online', '_blank')}
                    >
                      BOOK BEHANDLING
                    </Button>
                    <Button 
                      variant="outline"
                      onClick={() => {
                        const el = document.getElementById('behandlinger');
                        el?.scrollIntoView({ behavior: 'smooth' });
                      }}
                    >
                      LÆS MERE
                    </Button>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>

        {/* Subtle background element */}
        <div className="absolute right-0 top-0 -z-10 h-full w-1/2 overflow-hidden opacity-30 lg:opacity-80">
          <div className="relative h-full w-full">
            <img 
              src={getOptimizedImageUrl(images.physio, { width: 1200, quality: 80 })} 
              className="w-full h-full object-cover grayscale-[0.4]" 
              alt="Fysioterapi i LK STUDIO" 
            />
            <div className="absolute inset-0 bg-gradient-to-r from-[#F1E5DC] via-[#F1E5DC]/50 to-transparent" />
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <Section className="pt-0">
        <div className="grid md:grid-cols-2 gap-12 max-w-6xl mx-auto">
          {/* Screening */}
          <motion.div 
            whileHover={{ y: -10 }}
            className="bg-white p-8 md:p-12 rounded-[3rem] shadow-xl border border-[#CEBDAA]/20"
          >
            <div className="w-16 h-16 bg-[#F1E5DC] text-[#1C1B1A] rounded-2xl flex items-center justify-center mb-8">
              <Activity className="w-8 h-8" />
            </div>
            <h3 className="text-2xl font-black uppercase mb-6 text-[#1C1B1A]">Screening</h3>
            <p className="text-[#494540] leading-relaxed mb-6 font-medium">
              En fysioterapeutisk screening giver dig et overblik over din krops styrker, begrænsninger og potentiale. Relevant for dig, der har smerter, skal i gang med EMS eller ønsker en sikker start.
            </p>
            <ul className="space-y-3 text-[#494540] mb-8 font-medium">
              <li className="flex items-center gap-3"><ShieldCheck className="w-5 h-5 text-[#A8814E]" /> Kortlægning af fysisk tilstand</li>
              <li className="flex items-center gap-3"><ShieldCheck className="w-5 h-5 text-[#A8814E]" /> Forebyggelse af overbelastning</li>
              <li className="flex items-center gap-3"><ShieldCheck className="w-5 h-5 text-[#A8814E]" /> Målrettet træningsplan</li>
            </ul>
          </motion.div>

          {/* Genoptræning */}
          <motion.div 
            whileHover={{ y: -10 }}
            className="bg-[#090807] text-white p-8 md:p-12 rounded-[3rem] shadow-xl"
          >
            <div className="w-16 h-16 bg-white/10 text-white rounded-2xl flex items-center justify-center mb-8">
              <Zap className="w-8 h-8" />
            </div>
            <h3 className="text-3xl font-black uppercase mb-6">Genoptræning</h3>
            <p className="text-[#CEBDAA] leading-relaxed mb-6 font-medium">
              Vejen tilbage til funktion og hverdag. Afgørende efter skade, sygdom eller operation. Vi hjælper dig med at genvinde styrke, mobilitet og livskvalitet.
            </p>
            <ul className="space-y-3 text-[#F1E5DC]/80 mb-8 font-medium">
              <li className="flex items-center gap-3"><ShieldCheck className="w-5 h-5 text-[#A8814E]" /> Øget muskelstyrke & bevægelighed</li>
              <li className="flex items-center gap-3"><ShieldCheck className="w-5 h-5 text-[#A8814E]" /> Smertelindring</li>
              <li className="flex items-center gap-3"><ShieldCheck className="w-5 h-5 text-[#A8814E]" /> Forebyggelse af re-skader</li>
            </ul>
          </motion.div>
        </div>
      </Section>

      {/* Sportsmassage Section */}
      <Section className="bg-white">
        <div className="max-w-6xl mx-auto flex flex-col md:flex-row items-center gap-12 md:gap-20 md:py-12">
          <div className="w-full md:w-1/2 flex flex-col items-start text-left">
            <Badge>VELVÆRE & RESTITUTION</Badge>
            <h2 className="text-3xl font-black uppercase mb-8 leading-tight text-[#1C1B1A]">Sportsmassage</h2>
            <p className="text-lg text-[#494540] leading-relaxed mb-8 font-medium">
              Dybdegående massage målrettet muskelspændinger, ømhed og bevægebegrænsninger. Velegnet til både idrætsudøvere og dig med stillesiddende arbejde eller stress.
            </p>
            <div className="grid grid-cols-2 gap-6 mb-12">
              <div className="flex flex-col gap-2">
                <Heart className="w-6 h-6 text-[#A8814E]" />
                <p className="font-bold uppercase text-sm text-[#1C1B1A]">Øget cirkulation</p>
              </div>
              <div className="flex flex-col gap-2">
                <Activity className="w-6 h-6 text-[#A8814E]" />
                <p className="font-bold uppercase text-sm text-[#1C1B1A]">Bedre restitution</p>
              </div>
            </div>
            <Button variant="primary" className="rounded-2xl h-12 px-6 sm:h-16 sm:px-10 text-sm sm:text-base" onClick={() => window.open('https://booking.lkstudioems.com/book-online', '_blank')}>BOOK MASSAGE</Button>
          </div>
          <div className="w-full md:w-1/2">
            <div className="rounded-[3rem] md:rounded-[4rem] overflow-hidden shadow-2xl rotate-2">
              <img 
                src={getOptimizedImageUrl("https://assets.auxodev.dk/assets/766f1303-2002-4537-8cbb-d8bd7f1c5c0f/ed868891-2320-499e-96ec-6bf1fa318831.jpg", { width: 800, quality: 80 })} 
                alt="Sportsmassage" 
                className="w-full h-full object-cover" 
                loading="lazy"
              />
            </div>
          </div>
        </div>
      </Section>

      {/* About Specialist */}
      <Section className="pb-8 md:pb-20 lg:pb-32">
        <div className="bg-[#F1E5DC] rounded-[3rem] md:rounded-[4rem] p-6 sm:p-12 md:p-24 max-w-6xl mx-auto flex flex-col md:flex-row items-center gap-12 md:gap-16">
          <div className="w-full md:w-2/5">
            <div className="aspect-[3/4] rounded-[2.5rem] md:rounded-[3rem] overflow-hidden shadow-2xl -rotate-2 md:-rotate-3">
              <img 
                src={getOptimizedImageUrl(images.line, { width: 600, height: 800, fit: 'cover' })} 
                alt="Line - Fysioterapi" 
                className="w-full h-full object-cover" 
                loading="lazy"
              />
            </div>
          </div>
          <div className="w-full md:w-3/5 flex flex-col items-start text-left">
            <Heading subtitle="Din Specialist" className="mb-8">Mød Line</Heading>
            <p className="text-lg text-[#1C1B1A] font-black mb-6">
              Jeg er en erfaren fysioterapeut med speciale i muskuloskeletale problematikker.
            </p>
            <div className="space-y-6 text-[#494540] font-medium leading-relaxed mb-12">
              <p>
                Jeg har 12 års erfaring fra privatklinik og har behandlet både akutte og langvarige skader. Jeg ser kroppen som en helhed og er desuden fast instruktør i EMS-træning.
              </p>
              <ul className="space-y-2">
                <li>• Uddannet fysioterapeut (2012)</li>
                <li>• Exam. MPT – Muskuloskeletal Fysioterapi</li>
                <li>• Efteruddannet i sportsfysioterapi og funktionel træning</li>
              </ul>
            </div>
            <Button variant="outline" className="rounded-2xl text-sm sm:text-base" onClick={() => window.open('https://booking.lkstudioems.com/book-online', '_blank')}>BOOK TID HOS LINE</Button>
          </div>
        </div>
      </Section>
    </div>
  );
};

export default Fysioterapi;
